/**
 * Words for clarification questions, shared by the chat, the lists and the
 * approval warning so a question reads the same everywhere.
 */

export const STATUS_LABELS = {
  open: 'Awaiting an answer',
  answered: 'Answered',
  resolved: 'Resolved',
  closed: 'Closed',
};

/** Pill colours. The -50/-100/-700/-800 shades are the ones index.css remaps for dark mode. */
export const STATUS_PILL = {
  open: 'bg-amber-100 text-amber-800',
  answered: 'bg-blue-100 text-blue-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-gray-100 text-gray-700',
};

const ROLE_LABELS = {
  user: 'Claimant',
  admin: 'Admin',
  auditor: 'Auditor',
  regional_director: 'Regional Director',
  global_director: 'Global Director',
  finance_director: 'Finance Director',
};
export const roleLabel = (role) => ROLE_LABELS[role] || String(role || '').replace(/_/g, ' ');

export const isUnresolved = (thread) => thread?.status === 'open' || thread?.status === 'answered';

/** The key a claim's questions are grouped under - the same spelling the server stores. */
export const claimKey = (claimType, claimId) =>
  `${/^(perdiem|perdiemclaim|per_diem)$/i.test(String(claimType || '')) ? 'PerDiemClaim' : 'ExpenseClaim'}:${claimId}`;

/** "12 Oct 2026, 14:05" in the viewer's own timezone - the teams are spread across several. */
export const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** What the approver is shown about the questions still open, one per line. */
export const describeOpenQuestions = (questions = [], max = 5) => {
  const lines = questions.slice(0, max).map((q) => {
    const what = q.subjectType === 'report' ? 'The report' : (q.subjectLabel || 'A claim');
    const asked = q.question ? `"${q.question}"` : 'a question';
    const by = q.askedByName ? `asked by ${q.askedByName}` : 'asked';
    return `- ${what}: ${asked} (${by}; ${(STATUS_LABELS[q.status] || q.status || '').toLowerCase()})`;
  });
  if (questions.length > max) lines.push(`- and ${questions.length - max} more`);
  return lines.join('\n');
};

/**
 * Tell the rest of the page that a question changed (asked, answered, read,
 * resolved), so the bell and any badges refresh now instead of on their next
 * poll.
 */
export const CHANGED_EVENT = 'clarifications:changed';
export const announceChange = () => {
  try { window.dispatchEvent(new Event(CHANGED_EVENT)); } catch { /* no window in tests */ }
};

export const FILE_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
export const MAX_FILES = 5;
export const MAX_FILE_MB = 20;
export const MAX_MESSAGE_LENGTH = 4000;

/**
 * Files the server will refuse, caught before upload so the message is not
 * lost to a 400. The server still checks the bytes.
 */
export const fileProblems = (files = []) => {
  if (files.length > MAX_FILES) return `Attach at most ${MAX_FILES} files to one message.`;
  for (const f of files) {
    if (f.size > MAX_FILE_MB * 1024 * 1024) return `${f.name} is larger than ${MAX_FILE_MB} MB.`;
    if (!/\.(pdf|jpe?g|png)$/i.test(f.name || '') && !/^(application\/pdf|image\/(jpeg|png))$/.test(f.type || '')) {
      return `${f.name} is not a PDF, JPG or PNG file.`;
    }
  }
  return null;
};
